import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import type { RelatedKey } from './content/page-explore/related-tiles.catalog';
import type { BreadcrumbItem } from './seo-breadcrumbs';

/**
 * Per-route UI data registered via `Route.data` blocks in `app.routes.ts`.
 *
 * Wave 2 / Phase A introduced this service to consolidate former imperative
 * APIs (`PageHeadingService.setHeading`, inline `bottomCtaExtras` arrays)
 * into a single declarative source. Each route entry carries `heading` and
 * `bottomCta` keys; this service subscribes once to `NavigationEnd`, walks
 * the deepest active snapshot, and exposes readonly signals consumers can
 * read.
 *
 * Dynamic routes (blog post/topic/tag/level) need to override the heading
 * at runtime — the slug isn't known until input binding fires. They call
 * `setHeadingOverride()` from an effect; the override is cleared on every
 * NavigationEnd so it can't leak across pages.
 */

export interface RouteHeading {
  /** Title text for the page <h1>. Empty string suppresses the heading. */
  title: string;
  /** Optional lead paragraph rendered under the <h1>. */
  lead?: string;
  /**
   * When true, the heading renders as `<h1 class="visually-hidden">` so
   * SEO + a11y see a single page heading but the visual hero handles the
   * branded landing-page look. Currently only used by the home route.
   */
  visuallyHidden?: boolean;
}

export interface RouteBottomCta {
  /** Per-route heading text for the inline bottom CTA. */
  heading?: string;
  /**
   * Per-route body override for the inline bottom CTA. Routes about
   * something other than lessons (performance, x-ploration-jazz)
   * supply their own; lesson routes fall back to the locked body.
   */
  body?: string;
}

@Injectable({ providedIn: 'root' })
export class RouteUiDataService {
  private readonly router = inject(Router);

  private readonly _routeHeading = signal<RouteHeading | null>(null);
  private readonly _bottomCta = signal<RouteBottomCta | null>(null);
  private readonly _stageExtension = signal(false);
  private readonly _suppressHeaderCta = signal(false);
  private readonly _suppressFooterCta = signal(false);
  private readonly _measure = signal<'article' | undefined>(undefined);
  private readonly _headingOverride = signal<RouteHeading | null>(null);
  private readonly _related = signal<RelatedKey[]>([]);
  private readonly _breadcrumbs = signal<readonly BreadcrumbItem[]>([]);
  private readonly _breadcrumbOverride = signal<
    readonly BreadcrumbItem[] | null
  >(null);

  /**
   * The effective heading for the current route. Returns the runtime
   * override if set (for dynamic blog routes), otherwise the static
   * route data value, otherwise null.
   */
  readonly heading = computed<RouteHeading | null>(
    () => this._headingOverride() ?? this._routeHeading(),
  );
  readonly bottomCta = this._bottomCta.asReadonly();
  readonly related = this._related.asReadonly();

  /**
   * The breadcrumb trail for the current route: the runtime override (dynamic
   * blog routes) if set, else the route's static `data.breadcrumbs`, else [].
   * Static routes: this same list also feeds SEOService's BreadcrumbList
   * JSON-LD. `/blog/:slug` builds its own JSON-LD list in
   * `buildBlogPostSchemas` instead; the topic/tag/level routes' override adds
   * a leaf crumb here that their `data.breadcrumbs`-driven JSON-LD omits.
   */
  readonly breadcrumbs = computed<readonly BreadcrumbItem[]>(
    () => this._breadcrumbOverride() ?? this._breadcrumbs(),
  );

  /**
   * True when the active route declares `data.stageExtension: true`.
   *
   * Such a route hosts its OWN stage-scene extension (the home hero continues
   * the dark stage chrome before rendering its own `.groove-handoff`), so the
   * shell SUPPRESSES the default chrome→paper handoff it otherwise renders
   * immediately after the site chrome. Absent / false (every other route) =
   * the shell renders the handoff, giving every inner page the
   * stage-chrome → wavy-edge → paper composition with zero per-route wiring.
   *
   * Read the same declarative way as `heading` / `bottomCta`: walked off
   * the deepest active route snapshot once per `NavigationEnd`.
   */
  readonly stageExtension = this._stageExtension.asReadonly();

  /**
   * True when the active route declares `data.suppressHeaderCta: true`.
   *
   * The shell renders a persistent `<app-header-cta />` strip ("Questions
   * about lessons?") in the nav row of every route. Routes whose hero already
   * carries its own call/email conversion actions (home, x-ploration-jazz)
   * set this flag so the header strip doesn't duplicate that ask. Absent /
   * false (every other route) leaves the header CTA strip in place.
   */
  readonly suppressHeaderCta = this._suppressHeaderCta.asReadonly();

  /**
   * True when the active route declares `data.suppressFooterCta: true`.
   *
   * The shell renders the Explore tiles + bottom CTA pair after the routed
   * page content for every inner page. Home opts out — it renders its own
   * inline Explore section and bottom reprise — and sets this flag so the
   * shell skips the pair for that route. Absent / false (every inner page)
   * leaves the shell pair in control.
   */
  readonly suppressFooterCta = this._suppressFooterCta.asReadonly();

  /**
   * The active route's `data.measure` value, or `undefined` when absent.
   *
   * Only `'article'` exists today (`/blog/:slug`, spec §14.2 item 1). The
   * shell applies a `content--article` class to `<main class="content">`
   * when this is `'article'`, which sets `--shell-measure: var(--blog-measure)`
   * — read by `app-page-heading`, `app-breadcrumb-trail` and
   * `.page-footer-cta` (`max-width: var(--shell-measure, none)`) so a blog
   * post's heading, trail and bottom CTA share the article's reading-column
   * edges. Absent (every other route) leaves `--shell-measure` unset, so
   * those three fall back to `none` and keep the full shell-cap width.
   */
  readonly measure = this._measure.asReadonly();

  constructor() {
    // Seed the initial values from the current snapshot so the first
    // paint after SSR sees the correct data without waiting for a
    // NavigationEnd event.
    this.updateFromSnapshot();

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        // Drop any prior runtime heading/breadcrumb override before applying
        // the new route's static data — overrides are scoped to the route
        // that set them.
        this._headingOverride.set(null);
        this._breadcrumbOverride.set(null);
        this.updateFromSnapshot();
      });
  }

  /**
   * Runtime override for the page heading. Used by dynamic routes
   * (blog post / topic / tag / level) where the heading depends on a
   * route param resolved after navigation. Cleared automatically on
   * the next NavigationEnd.
   */
  setHeadingOverride(heading: RouteHeading): void {
    this._headingOverride.set(heading);
  }

  /** Runtime breadcrumb override for dynamic routes. Cleared on NavigationEnd. */
  setBreadcrumbOverride(items: readonly BreadcrumbItem[]): void {
    this._breadcrumbOverride.set(items);
  }

  private updateFromSnapshot(): void {
    let route: ActivatedRouteSnapshot | null =
      this.router.routerState.snapshot.root;
    let heading: RouteHeading | null = null;
    let bottomCta: RouteBottomCta | null = null;
    let stageExtension = false;
    let suppressHeaderCta = false;
    let suppressFooterCta = false;
    let measure: 'article' | undefined;
    let relatedData: RelatedKey[] | undefined;
    let breadcrumbs: readonly BreadcrumbItem[] | undefined;

    while (route) {
      const h = route.data['heading'] as RouteHeading | undefined;
      const b = route.data['bottomCta'] as RouteBottomCta | undefined;
      const s = route.data['stageExtension'] as boolean | undefined;
      const sc = route.data['suppressHeaderCta'] as boolean | undefined;
      const sfc = route.data['suppressFooterCta'] as boolean | undefined;
      const me = route.data['measure'] as 'article' | undefined;
      const r = route.data['related'] as RelatedKey[] | undefined;
      const bc = route.data['breadcrumbs'] as BreadcrumbItem[] | undefined;

      if (h) heading = h;
      if (b) bottomCta = b;
      if (s !== undefined) stageExtension = s;
      if (sc !== undefined) suppressHeaderCta = sc;
      if (sfc !== undefined) suppressFooterCta = sfc;
      if (me !== undefined) measure = me;
      if (r !== undefined) relatedData = r;
      if (bc !== undefined) breadcrumbs = bc;

      route = route.firstChild;
    }

    // Reset to null on every navigation so a route with no `data.bottomCta`
    // doesn't keep stale state from the previous route.
    this._routeHeading.set(heading);
    this._bottomCta.set(bottomCta);
    this._stageExtension.set(stageExtension);
    this._suppressHeaderCta.set(suppressHeaderCta);
    this._suppressFooterCta.set(suppressFooterCta);
    this._measure.set(measure);
    this._related.set(relatedData ?? []);
    this._breadcrumbs.set(breadcrumbs ?? []);
  }
}
